import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlowPickerMenu, type PickerSection } from './flow-picker-menu';

import type { Task } from '@/types/experience';

import { taskGroups } from '@/lib/task-groups';
import { trackCounter, type TaskTrack } from '@/lib/task-track';

/**
 * Previous / next task, along the experience's STRUCTURE — see `taskTrack` for why structural
 * and not flow-driven.
 *
 * ## Fixed width, deliberately
 *
 * The whole point is clicking through tasks quickly, and a control that moves between clicks is
 * one you stop being able to aim at. Two things could have moved it and neither does: the
 * counter is `3/7` or `↳2/3` rather than the pathway's name, and the crumb to its left is the
 * EXPERIENCE title, which does not change while you walk that experience. (The task title did
 * change, which is one reason it is no longer in the trail.)
 *
 * `tabular-nums` so the digits themselves do not shuffle the glyphs as the number grows.
 *
 * ## Both ends are dead ends
 *
 * An absent `prev`/`next` IS the disabled state — a track is bounded, and changing track is an
 * explicit act in the builder rather than something an arrow does under you.
 */
export default function TaskStepper({ track, tasks, currentTaskId, onGo, onAdd }: {
  track: TaskTrack;
  /** Every task, for the jump menu — the arrows stay on this track, the menu reaches all of it. */
  tasks: Task[];
  currentTaskId: string;
  onGo: (taskId: string) => void;
  /**
   * Make another task from the END of this track, and go to it.
   *
   * Optional because not every surface that shows a stepper can create — a read-only view
   * passes nothing and simply keeps the disabled arrow it always had.
   *
   * DUPLICATE is the one the owner actually reaches for, and the reason is worth keeping: a
   * task in a sequence usually shares its predecessor's intro text and images, so starting
   * from a copy is starting from the story rather than from nothing.
   */
  onAdd?: (mode: 'new' | 'duplicate') => void;
}) {
  const { t } = useTranslation();

  /** The end-of-track control: a menu, never a one-press create. See the arrow below. */
  const addMenu = (
    <FlowPickerMenu
      sections={[{ options: [
        { key: 'new', text: t('nav.stepper.addNew') },
        { key: 'duplicate', text: t('nav.stepper.addDuplicate') },
      ] }]}
      onChoose={key => onAdd?.(key as 'new' | 'duplicate')}
      ariaLabel={t('nav.stepper.addAria')}
      /* No filter: two fixed options, and "Find a task" above them was furniture borrowed from
         the jump menu below, which really does need one. */
      noMatchesLabel={t('nav.stepper.jumpNoMatches')}
      renderTrigger={props => (
        <button
          {...props}
          type='button'
          aria-label={t('nav.stepper.addAria')}
          /* The same 40px target the arrows have, so the row does not change shape at the end
             of a track — only what the last control means. */
          className='flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-ws-text hover:bg-ws-hover'
        >
          <Plus className='h-4 w-4' />
        </button>
      )}
    />
  );

  /**
   * The counter IS the way into the rest of the experience.
   *
   * The arrows deliberately never leave this track, which is what makes them trustworthy — but
   * it also means they cannot reach a pathway at all, and on a 40-task spine stepping is 40
   * clicks. So the position doubles as the trigger for a jump menu: it already says "where am
   * I", and opening it answers "where else is there".
   *
   * Built on the SAME list Flow's "go to" picker uses (`taskGroups`, through `FlowPickerMenu`),
   * so the two cannot disagree about which tasks are on the spine or what a pathway is called —
   * and so the search field, grouping and keyboard handling are one implementation rather than
   * a second one that drifts.
   */
  const sections = useMemo<PickerSection[]>(() => {
    const { spine, pathways } = taskGroups(tasks);
    const untitled = t('nav.stepper.jumpUntitledTask');
    const out: PickerSection[] = [];
    if (spine.length) {
      out.push({
        header: t('nav.stepper.jumpMainSequence'),
        options: spine.map(({ task, num }) => ({
          key: task.id,
          text: `${num}. ${task.title || untitled}`,
          selected: task.id === currentTaskId,
        })),
      });
    }
    for (const pathway of pathways) {
      out.push({
        header: pathway.name || t('nav.stepper.jumpUnnamedPathway'),
        options: pathway.members.map(task => ({
          key: task.id,
          text: task.title || untitled,
          selected: task.id === currentTaskId,
        })),
      });
    }
    return out;
  }, [tasks, currentTaskId, t]);

  // One task and nowhere to go: no arrows, and nothing to open a menu onto.
  if (track.members.length < 2 && sections.every(s => s.options.length <= 1)) return null;

  const where = track.pathwayName
    ? t('nav.stepper.inPathway', { name: track.pathwayName })
    : t('nav.stepper.inSequence');

  const arrow = (dir: 'prev' | 'next') => {
    const target = dir === 'prev' ? track.prev : track.next;
    /*
     * AT THE END, THE ARROW BECOMES A MENU — never a one-press create.
     *
     * The hazard the owner reported, in their words: paging quickly through versions and
     * ending up with six new ones. It is not really about speed. It is that ONE BUTTON does
     * two different things depending on where you are, and you cannot see which until you have
     * pressed it. A menu makes the last press non-destructive: mindless paging now ends on
     * something you can dismiss, and nothing was created.
     *
     * Only on `next`, and only where adding is possible. Going backwards has no such step.
     */
    if (dir === 'next' && !target && onAdd) return addMenu;
    const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
    return (
      <button
        type='button'
        disabled={!target}
        onClick={() => target && onGo(target.id)}
        /* The destination is NAMED, not just "next": a teacher using the keyboard should hear
           which task they are about to open and which track it is on, since the arrows never
           cross between tracks and that is the surprising part. */
        aria-label={target
          ? t(dir === 'prev' ? 'nav.stepper.previous' : 'nav.stepper.next', { title: target.title, where })
          : t(dir === 'prev' ? 'nav.stepper.atStart' : 'nav.stepper.atEnd', { where })}
        /* 40px, not 32. These are the controls meant for repeated tapping, and the bar they sit
           in is already 44px tall — so this spends height that was there anyway rather than
           widening anything. Measured before: arrows 32x32 and the counter 52x24, which clears
           WCAG's 24px minimum but leaves the counter exactly on the floor and everything well
           under the 44px both Apple and Material ask for. The icon inside is unchanged, so
           nothing looks heavier. */
        className='flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-ws-text hover:bg-ws-hover disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent'
      >
        <Icon className='h-4 w-4' />
      </button>
    );
  };

  return (
    <div className='flex shrink-0 items-center'>
      {arrow('prev')}
      <FlowPickerMenu
        sections={sections}
        onChoose={onGo}
        ariaLabel={t('nav.stepper.jumpAria')}
        filterPlaceholder={t('nav.stepper.jumpFilter')}
        noMatchesLabel={t('nav.stepper.jumpNoMatches')}
        renderTrigger={props => (
          <button
            {...props}
            type='button'
            /* The position AND the way to leave this track, in one control — see `sections`.
               `tabular-nums` so the digits do not shuffle its width as the number grows. */
            aria-label={t('nav.stepper.position', { index: track.index + 1, total: track.members.length, where })}
            title={track.pathwayName ?? undefined}
            className='flex h-10 min-w-[3.25rem] select-none items-center justify-center rounded-md px-1 text-xs tabular-nums text-ws-muted hover:bg-ws-hover hover:text-ws-text'
          >
            {trackCounter(track)}
          </button>
        )}
      />
      {arrow('next')}
    </div>
  );
}
